import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { ReactComponent as ExportIcon } from "../../../../assets/images/export.svg";
import {
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiDateField,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  OptionsDataType,
  RowProps,
} from "../../../../components";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import { MODULE_PERMISSIONS } from "../../../../helpers/constants";
import {
  arrayOfObjectsIncludesValue,
  buildActionMenuItems,
  checkFilterMatch,
  compareDates,
  convertStringArrayToOptions,
  convertToUserFormat,
  getUniqueLabelOptions,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  SearchUserDataResponse,
  UserCountry,
  UsersSearchResultKey,
} from "../../modelsAndSchemas/models";
import { searchUsersResultsSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { UsersSearchResultWrapper } from "./UsersSearchResults.style";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import { createUserSiteCoreKeys } from "../../../createUser/siteCore/SiteCoreKeys";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { usePermissions } from "../../../../hooks/usePermissions";

interface UsersSearchResultsProps {
  rows: SearchUserDataResponse[];
  isSearchClick: boolean;
  pageId: string;
  onUserSelect: (id: string) => void;
  handleEditUser: (id: string) => void;
  onHandleExport: () => void;
}

export const UsersSearchResults: FC<UsersSearchResultsProps> = ({
  rows,
  isSearchClick,
  pageId,
  onUserSelect,
  handleEditUser,
  onHandleExport,
}) => {
  const {
    commonLabels,
    fetchComponentTitle,
    useFetchLabel,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const tableRef = React.useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [uniqueStatusList, setUniqueStatusList] = useState<OptionsDataType[]>(
    [],
  );
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    SearchUserDataResponse[]
  >([]);

  const { isMobile } = useMediaQueries();

  const {
    results,
    allUsersHeader,
    userName,
    userEmail,
    roleLabel,
    countryLabel,
    serviceOffice,
    userStatus,
    createdDate,
    actionLabel,
    selectedCountry,
    selectedRoles,
    selectedServiceOffice,
    statusList,
  } = searchUsersResultsSiteCoreKeys;

  const getResultsLabel = useFetchLabel(results);

  const { control, reset, getValues, setValue } = useForm({
    mode: "all",
    defaultValues: {
      [UsersSearchResultKey.userName]: "",
      [UsersSearchResultKey.userEmail]: "",
      [UsersSearchResultKey.role]: "",
      [UsersSearchResultKey.country]: "",
      [UsersSearchResultKey.serviceOfficeName]: "",
      [UsersSearchResultKey.userMobileNumber]: "",
      [UsersSearchResultKey.userStatus]: "",
      [UsersSearchResultKey.createdDate]: "",
    },
  });

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  useEffect(() => {
    resetPagination(true);
    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = structuredClone(rows);

      localData.forEach((item: SearchUserDataResponse) => {
        item.roles = convertStringArrayToOptions(item.role);
        const statusValue =
          item.userStatus === true
            ? createUserSiteCoreKeys.statusActive
            : createUserSiteCoreKeys.statusInActive;

        item.userStatusKey = statusValue;
        item.userStatus = fetchValueByKeyOfDropdown(
          results,
          statusList,
          statusValue,
        );

        item.serviceOffices = convertStringArrayToOptions(
          item.serviceOfficeName,
        );

        item.countries =
          item.country?.map((item: UserCountry) => ({
            label: item.countryName,
            value: item.countryCode,
          })) ?? [];

        item.createdDate = item.createdTimeStamp;
      });

      const uniqueStatus = getUniqueLabelOptions(
        localData,
        UsersSearchResultKey.userStatus,
      );
      setUniqueStatusList(uniqueStatus);

      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
    }
  }, [rows, siteCoreLoading]);

  const filters = useWatch({ control });

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        userName,
        userEmail,
        role,
        country,
        serviceOfficeName,
        userStatus,
        createdDate: createdDateField,
      } = getValues();

      const filteredData =
        allUpdatedRows?.filter((item) => {
          const nameMatch = checkFilterMatch(
            userName,
            getValueOrBlank(item?.userName),
          );

          const emailAddressMatch = checkFilterMatch(userEmail, item.userEmail);

          const roleMatch = arrayOfObjectsIncludesValue(
            role,
            item.roles,
            "label",
          );

          const countryMatch = arrayOfObjectsIncludesValue(
            country,
            item.countries,
            "label",
          );

          const serviceOfficeMatch = arrayOfObjectsIncludesValue(
            serviceOfficeName,
            item.serviceOffices,
            "label",
          );

          const statusMatch =
            userStatus === "" || item.userStatus === userStatus;

          const isDateMatch = compareDates(
            createdDateField ?? "",
            item?.createdTimeStamp ?? "",
            getValueOrBlank(userCountryDateFormat),
          );

          return (
            nameMatch &&
            emailAddressMatch &&
            roleMatch &&
            countryMatch &&
            serviceOfficeMatch &&
            statusMatch &&
            isDateMatch
          );
        }) ?? [];
      setFilteredResults([...filteredData]);
      resetPagination();
    }
  }, [filters]);

  const columns = [
    {
      id: UsersSearchResultKey.userName,
      label: getResultsLabel(userName),
      isEmptyValue: true,
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          onClick={() => {
            onUserSelect(row?.id);
          }}
          id={`${pageId}-view-user-${row.id}`}
          data-testid="view-user-link"
        >
          <MuiBox>
            <MuiEllipseLabel
              labelTypoClassName="Link"
              text={row.userName}
              labelTypoVariant="h3"
              placement="bottom-start"
              pointerCursor
            />
          </MuiBox>
        </MuiLink>
      ),
    },
    {
      id: UsersSearchResultKey.userEmail,
      label: getResultsLabel(userEmail),
      isSortVisible: true,
      isEmptyValue: true,
      className: "min-width-cell",
    },
    {
      id: UsersSearchResultKey.role,
      label: getResultsLabel(roleLabel),
      isEmptyValue: true,
      isSortVisible: true,
      sortKeyName: "label",
      objectKeyName: "roles",
      className: "min-width-cell",
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={row.roles}
          id={"selected-roles"}
          isEditable={false}
          headerLabel={getResultsLabel(selectedRoles)}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: UsersSearchResultKey.country,
      label: getResultsLabel(countryLabel),
      isEmptyValue: true,
      isSortVisible: true,
      sortKeyName: "label",
      objectKeyName: "countries",
      className: "min-width-cell",
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={row.countries}
          id={"selected-country"}
          isEditable={false}
          headerLabel={getResultsLabel(selectedCountry)}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: UsersSearchResultKey.serviceOfficeName,
      label: getResultsLabel(serviceOffice),
      isSortVisible: true,
      sortKeyName: "label",
      objectKeyName: "serviceOffices",
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={row.serviceOffices}
          id={"selected-service-office"}
          isEditable={false}
          headerLabel={getResultsLabel(selectedServiceOffice)}
          isDialogTitleWithEllipsis
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },

    {
      id: UsersSearchResultKey.userStatus,
      label: getResultsLabel(userStatus),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => (
        <StatusBadge status={row.userStatusKey} label={row.userStatus} />
      ),
    },
    {
      id: UsersSearchResultKey.createdDate,
      label: getResultsLabel(createdDate),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => {
        return (
          <>
            {convertToUserFormat(row.createdTimeStamp, userCountryDateFormat)}
          </>
        );
      },
    },
    {
      id: "action",
      label: getResultsLabel(actionLabel),
      render: (row: RowProps) => {
        const menuItems = buildActionMenuItems(
          row,
          [
            {
              permissionKey: MODULE_PERMISSIONS.EDIT_USERS,
              create: (row) => ({
                onClick: () => handleEditUser(row?.id),
                dataTestId: "edit",
                label: commonLabels.edit,
                ariaLabel: commonLabels.edit,
              }),
            },
          ],
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const tableHeaderForm = [
    <FormController
      control={control}
      name={UsersSearchResultKey.userName}
      key={UsersSearchResultKey.userName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${UsersSearchResultKey.userName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      name={UsersSearchResultKey.userEmail}
      key={UsersSearchResultKey.userEmail}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${UsersSearchResultKey.userEmail}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      name={UsersSearchResultKey.role}
      key={UsersSearchResultKey.role}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${UsersSearchResultKey.role}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      name={UsersSearchResultKey.country}
      key={UsersSearchResultKey.country}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${UsersSearchResultKey.country}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      name={UsersSearchResultKey.serviceOfficeName}
      key={UsersSearchResultKey.serviceOfficeName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${UsersSearchResultKey.serviceOfficeName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,

    <FormController
      control={control}
      key={UsersSearchResultKey.userStatus}
      name={UsersSearchResultKey.userStatus}
    >
      <MuiSelect
        label=""
        options={uniqueStatusList}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${UsersSearchResultKey.userStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={UsersSearchResultKey.createdDate}
      key={UsersSearchResultKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          name={UsersSearchResultKey.createdDate}
          clearable
          value={
            getValues(UsersSearchResultKey.createdDate) !== ""
              ? dayjs(
                  getValues(UsersSearchResultKey.createdDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              UsersSearchResultKey.createdDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <></>, //for action column
  ];

  const ExportButton = (
    <MuiButtonTertiary onClick={onHandleExport}>
      <ExportIcon />
      {commonLabels.export}
    </MuiButtonTertiary>
  );
  return (
    <UsersSearchResultWrapper>
      <MuiBox className="result-header" data-testid="users-search-results">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {isSearchClick
            ? fetchComponentTitle(results)
            : getResultsLabel(allUsersHeader)}
        </MuiTypography>
        {rows.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="selected-count">
            {rows.length}
          </MuiTypography>
        )}
        {!isMobile && (
          <MuiBox textAlign="right" flexGrow={1}>
            {ExportButton}
          </MuiBox>
        )}
      </MuiBox>
      {isMobile && <MuiBox className="export-button">{ExportButton}</MuiBox>}
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }}>
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={tableHeaderForm}
            columns={columns}
            tableResults={[...filteredResults]}
            noDataInApi={rows.length === 0}
            noResultMessage={commonLabels.noResult}
            priorityResults={[]}
            isLastColumnSticky
          />
        </MuiGrid>
      </MuiGrid>
    </UsersSearchResultWrapper>
  );
};
